import { Component, OnInit, afterNextRender, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { FeriasBaseComponent } from '../../ferias-base.component';
import { ConfirmationService } from 'primeng/api';
import { SituacaoParcelaFeriasEnum } from 'src/app/components/shared/enums/situacao-parcela-ferias.enum';
import { addDays } from 'date-fns';
import { ExercicioSelecionado, ExerciciosService, ParcelaFerias, ProgramarFerias } from 'src/app/services/exercicios.service';
import { AlertMessageService } from '@sigepe/sigepe-template';
import { getDateFromYYYYMMDD, isValidDate } from 'src/app/components/shared/date-utils';
import { DataFormatadaPipe } from 'src/app/components/shared/pipes/dataFormatada.pipe';
import { SituacaoFuncionalEnum } from 'src/app/components/shared/enums/situacao-funcional.enum';
import { Message } from 'src/app/components/shared/message';
import { formatarMensagem } from 'src/app/components/shared/string-utils';

type ExercicioSelecionadoNavegacao = ExercicioSelecionado & {
    origemTela?: string;
};

export interface ParcelaFeriasEmProgramacao {
    id?: number;
    ordem: number;
    dataInicio: Date;
    validacao: {
        sobreposto: boolean;
        foraOrdem: boolean;
        foraJanelaProgramacao: boolean;
        quantidadeDiasIgualZero: boolean;
        dataInvalida: boolean;
    };
    quantidadeDias: number;
    dataFim: Date;
    situacao: {
        //Vindo da enum SituacaoParcelaFeriasEnum
        descricao: string;
        codigoCor: string;
    };
    adiantamentoSalarial: {
        valor: boolean;
        selecaoDesabilitada: boolean;
    };
    abonoPecuniario: {
        valor: boolean;
        selecaoDesabilitada: boolean;
    };
    adiantamentoDecimoTerceiro: {
        valor: boolean;
        selecaoDesabilitada: boolean;
    };
    parcelasDesconto: number;
}

@Component({
    selector: 'app-programar-ferias',
    templateUrl: './programar-ferias.component.html',
    styleUrls: ['./programar-ferias.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ProgramarFeriasComponent extends FeriasBaseComponent implements OnInit {
    private readonly chaveEstadoConsultaGerencial = 'consultaGerencialExercicioSelecionado';
    submetido = false;
    houvePreenchimento = false;

    parcelasFerias: ParcelaFeriasEmProgramacao[];

    adicionaisSelecionados?: string[];

    selectedValues: string[] = ['abonoPecuniario', 'adiantamentoDecimoTerceiro'];
    selectedValue: string;

    exercicioSelecionado: ExercicioSelecionado;
    veioDaConsultaGerencial = false;

    alocacaoSaldoDiasIncompleta = false;
    somaquantidadeDiasParcelas = 0;
    dataMinimaParcela: Date;
    dataMaximaParcela: Date;

    justificativaParaAgendarFerias = '';
    justificativaInvalida = false;
    justificativaAcumuloFerias = '';

    opcoesParcelasDesconto: any[] = [];

    constructor(
        private exerciciosService: ExerciciosService,
        alertaMsgService: AlertMessageService,
        private router: Router,
        private confirmationService: ConfirmationService,
        private dataFormatadaPipe: DataFormatadaPipe
    ) {
        super(alertaMsgService);

        const navigationData = this.obterDadosNavegacao();
        this.exercicioSelecionado = navigationData;
        this.veioDaConsultaGerencial = navigationData?.origemTela === 'consulta-gerencial' || this.ehFluxoConsultaGerencial();

        //Cria o array com as opções de parcelas para desconto
        const quantidadeParcelasPagamento = this.exercicioSelecionado?.parametrosFeriasOrgao?.quantidadeParcelasPagamento || 3;
        for (let index = 1; index <= quantidadeParcelasPagamento; index++) {
            this.opcoesParcelasDesconto.push(index);
        }

        afterNextRender(() => {
            this.configurarCabecalhoFluxo();
            this.rolarParaTopo();
        });
    }

    obterDataAtual(): Date {
        const dataAtual = new Date();
        dataAtual.setHours(0, 0, 0, 0);
        return dataAtual;
    }

    get regimeCeletistaOuAnistiado(): boolean {
        return SituacaoFuncionalEnum.CLT.id === this.exercicioSelecionado.siglaRegime || SituacaoFuncionalEnum.ANISTIADO.id === this.exercicioSelecionado.siglaRegime;
    }

    get ministroPresidenteOuVice(): boolean {
        return (SituacaoFuncionalEnum.ESTATUTARIO.id === this.exercicioSelecionado.siglaRegime || SituacaoFuncionalEnum.NATUREZA_ESPECIAL.id === this.exercicioSelecionado.siglaRegime) && this.exercicioSelecionado.codigoSituacaoFuncional === 7;
    }

    get msgParcelasSobrepostas(): string {
        return Message.ERRO_PARCELAS_COM_DATAS_SOBREPOSTAS;
    }

    get msgParcelasForaDeOrdem(): string {
        return Message.ERRO_DATA_INICIO_PARCELA_DEVE_SER_POSTERIOR;
    }

    get msgQuantidadeDiasIgualZero(): string {
        return Message.ERRO_DURACAO_MINIMA_PARCELA;
    }

    get msgAlocacaoSaldoDiasIncompleta(): string {
        return formatarMensagem(Message.ERRO_SOMA_PARCELAS_DIFERENTE_DO_SALDO, {
            totalDias: this.somaquantidadeDiasParcelas.toString(),
            saldoDias: this.exercicioSelecionado.saldoDias.toString()
        });
    }

    get msgCamposObrigatoriosNaoPreenchidos(): string {
        return Message.ERRO_CAMPOS_OBRIGATORIOS_NAO_PREENCHIDOS;
    }

    get msgCeletistaAnistiadoComParcelaForaPeriodoProgramacao(): string {
        return formatarMensagem(Message.ERRO_DATAS_PARCELA_FORA_DO_INTERVALO, {
            dataInicio: this.dataFormatadaPipe.transform(this.dataMinimaParcela),
            dataFim: this.dataFormatadaPipe.transform(this.dataMaximaParcela)
        });
    }

    get msgNaoCeletistaAnistiadoComParcelaForaPeriodoProgramacao(): string {
        return formatarMensagem(Message.ERRO_DATA_INICIO_PARCELA_FORA_DO_INTERVALO, {
            dataInicio: this.dataFormatadaPipe.transform(this.dataMinimaParcela),
            dataFim: this.dataFormatadaPipe.transform(this.dataMaximaParcela)
        });
    }

    get msgJustificativaObrigatoria(): string {
        return Message.ERRO_JUSTIFICATIVA_OBRIGATORIA;
    }

    get umTercoSaldoFerias(): number {
        return Math.ceil(this.exercicioSelecionado.saldoDias / 3);
    }

    get qtdDiasMinimoParaAbono(): number {
        return this.umTercoSaldoFerias + 1;
    }

    get quantidadeMaximaParcelasPermitida(): number {
        return this.exercicioSelecionado.quantidadeMaximaParcelas ?? 3;
    }

    ngOnInit() {
        //Determinar a data mínima para a todas as parcelas
        const dataInicioPeriodoProgramacao = getDateFromYYYYMMDD(this.exercicioSelecionado.dataInicioPeriodoProgramacaoDisponivel ? this.exercicioSelecionado.dataInicioPeriodoProgramacaoDisponivel : this.exercicioSelecionado.dataInicioPeriodoProgramacao);
        const dataHoje = this.obterDataAtual();

        this.dataMinimaParcela = dataInicioPeriodoProgramacao;

        //A data mínima da parcela tem de começar no mês seguinte à data de hoje
        const inicioProgramacaoAnteriorDataAtual = dataInicioPeriodoProgramacao <= dataHoje;
        const inicioProgramacaoEstaContidoMesAtual = dataInicioPeriodoProgramacao.getMonth() === dataHoje.getMonth() && dataInicioPeriodoProgramacao.getFullYear() === dataHoje.getFullYear();
        if (inicioProgramacaoAnteriorDataAtual || inicioProgramacaoEstaContidoMesAtual) {
            this.dataMinimaParcela = dataHoje;
            this.dataMinimaParcela.setMonth(dataHoje.getMonth() + 1);
            this.dataMinimaParcela.setDate(1);
        }

        this.dataMaximaParcela = getDateFromYYYYMMDD(this.exercicioSelecionado.dataFimPeriodoProgramacaoDisponivel ? this.exercicioSelecionado.dataFimPeriodoProgramacaoDisponivel : this.exercicioSelecionado.dataFimPeriodoProgramacao);

        //Criação da primeira parcela
        this.parcelasFerias = [
            {
                ordem: 1,
                dataInicio: undefined,
                validacao: {
                    sobreposto: false,
                    foraOrdem: false,
                    foraJanelaProgramacao: false,
                    quantidadeDiasIgualZero: false,
                    dataInvalida: false
                },
                quantidadeDias: undefined,
                dataFim: undefined,
                situacao: {
                    descricao: SituacaoParcelaFeriasEnum.NAO_SOLICITADA.descricao,
                    codigoCor: SituacaoParcelaFeriasEnum.NAO_SOLICITADA.extras.codigoCor
                },
                adiantamentoSalarial: {
                    valor: false,
                    selecaoDesabilitada: false
                },
                abonoPecuniario: {
                    valor: false,
                    selecaoDesabilitada: true
                },
                adiantamentoDecimoTerceiro: {
                    valor: false,
                    selecaoDesabilitada: false
                },
                parcelasDesconto: 1
            }
        ];
    }

    validarJustificativa() {
        if (!this.justificativaParaAgendarFerias || this.justificativaParaAgendarFerias.trim().length < 10 || this.justificativaParaAgendarFerias.trim().length > 300) {
            this.justificativaInvalida = true;
        } else {
            this.justificativaInvalida = false;
        }
    }

    onJustificativaChange(event) {
        this.houvePreenchimento = true;
        this.justificativaParaAgendarFerias = event.target.value ? event.target.value.trim() : '';
    }

    confirmarVoltar() {
        if (this.houvePreenchimento) {
            this.confirmationService.confirm({
                key: 'confirmPadrao',
                header: 'Confirmação',
                message: Message.CONFIRMACAO_ABANDONAR_TELA_COM_PERDA_DE_DADOS,
                accept: async () => {
                    this.voltar();
                }
            });
        } else {
            this.voltar();
        }
    }

    voltar() {
        if (this.veioDaConsultaGerencial) {
            sessionStorage.removeItem(this.chaveEstadoConsultaGerencial);
            this.router.navigate(['/secure/consulta-gerencial']);
            return;
        }
        this.router.navigate(['/secure/gerenciar-ferias/consultar-exercicios']);
    }

    private obterDadosNavegacao(): ExercicioSelecionadoNavegacao | null {
        const navigationData = this.router.getCurrentNavigation()?.extras?.state?.data as ExercicioSelecionadoNavegacao;
        if (navigationData) {
            return navigationData;
        }

        const estadoPersistido = this.recuperarEstadoConsultaGerencial();
        if (this.ehFluxoConsultaGerencial()) {
            return estadoPersistido || history.state?.data || null;
        }

        return history.state?.data || estadoPersistido || null;
    }

    private recuperarEstadoConsultaGerencial(): ExercicioSelecionado | null {
        try {
            const estadoSerializado = sessionStorage.getItem(this.chaveEstadoConsultaGerencial);
            return estadoSerializado ? JSON.parse(estadoSerializado) : null;
        } catch {
            return null;
        }
    }

    private ehFluxoConsultaGerencial(): boolean {
        return this.router.url.includes('/secure/consulta-gerencial/');
    }

    private configurarCabecalhoFluxo(): void {
        if (this.veioDaConsultaGerencial) {
            this.alterarTituloTemplate('Consulta Gerencial');
            this.alterarTextoBreadcrumb(1, 'Consulta Gerencial');
            return;
        }

        this.alterarTituloTemplate('Gerenciar Férias');
        this.alterarTextoBreadcrumb(1, 'Gerenciar Férias - Buscar Agente Público');
    }

    existeSobreposicaoDeParcelas(): boolean {
        return this.parcelasFerias.some(parcela => {
            return parcela.validacao.sobreposto;
        });
    }

    existeParcelaForaOrdem(): boolean {
        return this.parcelasFerias.some(parcela => {
            return parcela.validacao.foraOrdem;
        });
    }

    existeCampoObrigatorioNaoInformado(): boolean {
        return this.parcelasFerias.some(parcela => {
            return !parcela.dataInicio || (!parcela.quantidadeDias && parcela.quantidadeDias !== 0) || (parcela.adiantamentoSalarial.valor && !parcela.parcelasDesconto);
        });
    }

    existeParcelaComQuantidadeDiasIgualZero(): boolean {
        return this.parcelasFerias.some(parcela => {
            return parcela.validacao.quantidadeDiasIgualZero;
        });
    }

    existeParcelaForaPeriodoProgramacao(): boolean {
        return this.parcelasFerias.some(parcela => {
            return parcela.dataInicio && parcela.validacao.foraJanelaProgramacao;
        });
    }

    confirmarProgramacao() {
        this.submetido = true;
        this.clearMessages();

        let podeProgramar = true;

        //Validação da quantidade mínima de dias
        if (this.existeParcelaComQuantidadeDiasIgualZero()) {
            this.getAlertaMsgService().error(this.msgQuantidadeDiasIgualZero);
            podeProgramar = false;
        }

        // Validação da soma de dias das parcelas
        if (this.alocacaoSaldoDiasIncompleta) {
            this.getAlertaMsgService().error(this.msgAlocacaoSaldoDiasIncompleta);
            podeProgramar = false;
        }

        // Validação da justificativa
        this.validarJustificativa();
        if (this.justificativaInvalida) {
            this.getAlertaMsgService().error(this.msgJustificativaObrigatoria);
            podeProgramar = false;
        }

        //Validações impeditivas
        if (this.existeCampoObrigatorioNaoInformado()) {
            //Reseta todos os campos com data em branco
            this.parcelasFerias.forEach(parcela => {
                if (!parcela.dataInicio) {
                    parcela.validacao.foraJanelaProgramacao = false;
                    parcela.validacao.dataInvalida = false;
                }
            });

            this.getAlertaMsgService().error(this.msgCamposObrigatoriosNaoPreenchidos);
            podeProgramar = false;
        }
        if (this.existeSobreposicaoDeParcelas()) {
            this.getAlertaMsgService().error(this.msgParcelasSobrepostas);
            podeProgramar = false;
        }
        if (this.existeParcelaForaOrdem()) {
            this.getAlertaMsgService().error(this.msgParcelasForaDeOrdem);
            podeProgramar = false;
        }
        if (this.existeParcelaForaPeriodoProgramacao()) {
            if (this.regimeCeletistaOuAnistiado) {
                this.getAlertaMsgService().error(this.msgCeletistaAnistiadoComParcelaForaPeriodoProgramacao);
            } else {
                this.getAlertaMsgService().error(this.msgNaoCeletistaAnistiadoComParcelaForaPeriodoProgramacao);
            }
            podeProgramar = false;
        }

        if (podeProgramar) {
            this.confirmationService.confirm({
                key: 'confirmPadrao',
                header: 'Confirmação',
                message: formatarMensagem(Message.CONFIRMACAO_PROGRAMAR_FERIAS, {
                    anoExercicio: this.exercicioSelecionado.anoExercicio + ''
                }),
                accept: async () => {
                    this.programar();
                }
            });
        }
    }

    programar() {
        const parcelasProgramadas: ParcelaFerias[] = [];

        this.parcelasFerias.forEach(parcela => {
            parcelasProgramadas.push({
                id: parcela.id,
                ordem: parcela.ordem,
                dataInicio: parcela.dataInicio,
                quantidadeDias: parcela.quantidadeDias,
                adiantamentoSalarial: parcela.adiantamentoSalarial.valor,
                abonoPecuniario: parcela.abonoPecuniario.valor,
                adiantamentoDecimoTerceiro: parcela.adiantamentoDecimoTerceiro.valor,
                parcelasDesconto: parcela.adiantamentoSalarial.valor ? parcela.parcelasDesconto : null
            });
        });
        const programarFerias: ProgramarFerias = {
            parcelas: parcelasProgramadas,
            justificativaParaAgendarFerias: this.justificativaParaAgendarFerias
        };

        this.exerciciosService.programarFerias(this.exercicioSelecionado.idFerias, programarFerias).subscribe(data => {
            if (data.sucessoEnvioMensageria) {
                this.getAlertaMsgService().setAwatingSuccessMessage('Férias programadas com sucesso. Só será possível programar ou reprogramar férias de outro exercício após a chefia homologar ou recusar a solicitação feita neste exercício.');
            } else {
                this.getAlertaMsgService().setAwatingWarnMessage(Message.AVISO_ACAO_REALIZADA_SEM_NOTIFICACAO_AUTOMATICA);
            }
            if (this.veioDaConsultaGerencial) {
                sessionStorage.setItem('consultaGerencialAtualizarAposAcao', 'true');
            }
            this.voltar();
        });
    }

    inputDataInicioChange(parcela: ParcelaFeriasEmProgramacao, event) {
        this.houvePreenchimento = true;

        if (parcela.dataInicio || event.target.value === '') {
            this.dataInicioChange(parcela);
        } else if (event.target.value) {
            if (event.target.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                //Verificar se o event.target.value que está no formado dd/mm/yyyy é uma data válida
                const partesData = event.target.value.split('/');
                if (isValidDate(parseInt(partesData[2]), parseInt(partesData[1]), parseInt(partesData[0]))) {
                    parcela.validacao.foraJanelaProgramacao = true;
                    parcela.validacao.dataInvalida = false;
                    //Apaga a data de término
                    parcela.dataFim = undefined;
                } else {
                    parcela.validacao.foraJanelaProgramacao = false;
                    parcela.validacao.dataInvalida = true;
                    //Apaga a data de término
                    parcela.dataFim = undefined;
                }
            }
        }
    }

    blurDataInicioChange(parcela: ParcelaFeriasEmProgramacao, event) {
        this.houvePreenchimento = true;

        if (!parcela.dataInicio && event.target.value !== '' && !event.target.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            parcela.validacao.foraJanelaProgramacao = false;
            parcela.validacao.dataInvalida = true;

            //Apaga a data de término
            parcela.dataFim = undefined;
        }
    }

    dataInicioChange(parcela: ParcelaFeriasEmProgramacao) {
        this.houvePreenchimento = true;

        //Adiantamento de 13º só é possível quando a data de inicio for até o dia 30/06
        if (!parcela.dataInicio || parcela.dataInicio.getMonth() + 1 > 6) {
            parcela.adiantamentoDecimoTerceiro.valor = false;
            parcela.adiantamentoDecimoTerceiro.selecaoDesabilitada = true;
        } else {
            parcela.adiantamentoDecimoTerceiro.selecaoDesabilitada = false;
        }

        this.calcularDataFim(parcela);
    }

    quantidadeDiasChange(parcela: ParcelaFeriasEmProgramacao, criarNovasParcelas = true) {
        this.houvePreenchimento = true;

        //Transformar a quantidadeDias para number
        parcela.quantidadeDias = parcela.quantidadeDias || parcela.quantidadeDias === 0 ? parseInt('' + parcela.quantidadeDias) : undefined;

        if (this.regimeCeletistaOuAnistiado) {
            //CLT/ANS: Não pode pedir abono pecuniário se a quantidade de dias for menor que 1/3 do saldo + 1 dia
            parcela.abonoPecuniario.selecaoDesabilitada = false;
            if (!parcela.quantidadeDias || parcela.quantidadeDias < this.qtdDiasMinimoParaAbono) {
                parcela.abonoPecuniario.valor = false;
                parcela.abonoPecuniario.selecaoDesabilitada = true;
            }
        }

        parcela.validacao.quantidadeDiasIgualZero = false;
        //Não pode ter quantidade de dias = 0
        if (parcela.quantidadeDias === 0) {
            parcela.validacao.quantidadeDiasIgualZero = true;
        }

        this.calcularDataFim(parcela, criarNovasParcelas);

        this.verificarAlocacaoSaldoDias();
    }

    parcelasDescontoChange() {
        this.houvePreenchimento = true;
    }

    adiantamentoSalarialChange(parcelaAlterada: ParcelaFeriasEmProgramacao) {
        if (parcelaAlterada.adiantamentoSalarial.valor) {
            parcelaAlterada.parcelasDesconto = 1;
        } else {
            parcelaAlterada.parcelasDesconto = null;
        }
        this.houvePreenchimento = true;
    }

    abonoPecuniarioChange(parcelaAlterada: ParcelaFeriasEmProgramacao) {
        this.houvePreenchimento = true;

        //Desmarca todos os outros
        if (parcelaAlterada.abonoPecuniario.valor) {
            this.parcelasFerias.forEach(parcela => {
                if (parcelaAlterada.ordem !== parcela.ordem) {
                    if (parcela.abonoPecuniario.valor) {
                        parcela.abonoPecuniario.valor = false;
                        this.abonoPecuniarioChange(parcela);
                    }

                    parcela.abonoPecuniario.valor = false;
                }
            });

            parcelaAlterada.quantidadeDias = parcelaAlterada.quantidadeDias - this.umTercoSaldoFerias;
        } else {
            parcelaAlterada.quantidadeDias = parcelaAlterada.quantidadeDias + this.umTercoSaldoFerias;
        }

        this.calcularDataFim(parcelaAlterada);
    }

    adiantamentoDecimoTerceiroChange(parcelaAlterada: ParcelaFeriasEmProgramacao) {
        this.houvePreenchimento = true;

        //Desmarca todos os outros
        if (parcelaAlterada.adiantamentoDecimoTerceiro.valor) {
            this.parcelasFerias.forEach(parcela => {
                if (parcelaAlterada.ordem !== parcela.ordem) {
                    parcela.adiantamentoDecimoTerceiro.valor = false;
                }
            });
        }
    }

    criarNovasParcelas() {
        let totalquantidadeDiasParcelas = 0;
        this.parcelasFerias.forEach(parcela => {
            totalquantidadeDiasParcelas += parcela.quantidadeDias;
            //Soma 1/3 do saldo de férias se tiver abono pecuniário
            if (parcela.abonoPecuniario.valor) {
                totalquantidadeDiasParcelas += this.umTercoSaldoFerias;
            }
        });

        if (totalquantidadeDiasParcelas && totalquantidadeDiasParcelas < this.exercicioSelecionado.saldoDias && this.parcelasFerias.length < this.quantidadeMaximaParcelasPermitida) {
            this.parcelasFerias.push({
                ordem: this.parcelasFerias.length + 1,
                dataInicio: undefined,
                validacao: {
                    sobreposto: false,
                    foraOrdem: false,
                    foraJanelaProgramacao: false,
                    quantidadeDiasIgualZero: false,
                    dataInvalida: false
                },
                quantidadeDias: this.exercicioSelecionado.saldoDias - totalquantidadeDiasParcelas,
                dataFim: undefined,
                situacao: {
                    descricao: SituacaoParcelaFeriasEnum.NAO_SOLICITADA.descricao,
                    codigoCor: SituacaoParcelaFeriasEnum.NAO_SOLICITADA.extras.codigoCor
                },
                adiantamentoSalarial: {
                    valor: false,
                    selecaoDesabilitada: false
                },
                abonoPecuniario: {
                    valor: false,
                    selecaoDesabilitada: false
                },
                adiantamentoDecimoTerceiro: {
                    valor: false,
                    selecaoDesabilitada: false
                },
                parcelasDesconto: 1
            });

            this.quantidadeDiasChange(this.parcelasFerias.at(-1));
        }
    }

    calcularDataFim(parcela: ParcelaFeriasEmProgramacao, criarNovasParcelas = true) {
        if (!parcela.quantidadeDias || !parseInt(parcela.quantidadeDias as any)) {
            parcela.dataFim = undefined;
        } else {
            if (!parcela.dataInicio) {
                parcela.dataFim = undefined;
            }

            if (criarNovasParcelas) {
                this.criarNovasParcelas();
            }

            if (parcela.dataInicio) {
                parcela.dataFim = addDays(parcela.dataInicio, parcela.quantidadeDias - 1);
            }
        }

        this.validarPeriodos();
    }

    confirmarExclusaoParcela(parcela: ParcelaFeriasEmProgramacao) {
        this.confirmationService.confirm({
            key: 'confirmPadrao',
            header: 'Confirmação de exclusão',
            message: Message.CONFIRMACAO_EXCLUIR_PARCELA,
            accept: () => {
                this.excluirParcela(parcela);
            }
        });
    }

    excluirParcela(parcelaASerExcluida: ParcelaFeriasEmProgramacao) {
        //Primeiro desmarca o abono pecuniário da parcela a ser excluída
        if (parcelaASerExcluida.abonoPecuniario.valor) {
            parcelaASerExcluida.abonoPecuniario.valor = false;
            this.abonoPecuniarioChange(parcelaASerExcluida);
        }

        //Excluir a parcela
        this.parcelasFerias.splice(parcelaASerExcluida.ordem - 1, 1);

        //Atualiza a ordem de cada parcela
        this.parcelasFerias.forEach((parcela, indice) => {
            parcela.ordem = indice + 1;
        });

        //Se a parcela anterior estiver com abono pecuniário
        if (this.parcelasFerias.at(-1).abonoPecuniario.valor) {
            this.parcelasFerias.at(-1).quantidadeDias += parcelaASerExcluida.quantidadeDias + this.umTercoSaldoFerias;
            this.quantidadeDiasChange(this.parcelasFerias.at(-1), false);

            //Marca novamente o abono pecuniário da última parcela
            this.abonoPecuniarioChange(this.parcelasFerias.at(-1));
        } else {
            //Adiciona a quantidade de dias da parcela excluída para a última parcela do exercício
            this.parcelasFerias.at(-1).quantidadeDias += parcelaASerExcluida.quantidadeDias;
            this.quantidadeDiasChange(this.parcelasFerias.at(-1), false);
        }

        //Verifica se os periodos estão corretos
        this.validarPeriodos();

        this.verificarAlocacaoSaldoDias();
    }

    verificarAlocacaoSaldoDias() {
        let faltaPreenchimento = false;
        let quantidadeMinimaInformada = true;
        this.somaquantidadeDiasParcelas = 0;

        this.parcelasFerias.forEach(parcela => {
            if (parcela.quantidadeDias || parcela.quantidadeDias === 0) {
                this.somaquantidadeDiasParcelas += parcela.quantidadeDias;

                if (parcela.abonoPecuniario.valor) {
                    this.somaquantidadeDiasParcelas += this.umTercoSaldoFerias;
                }

                if (parcela.validacao.quantidadeDiasIgualZero) {
                    quantidadeMinimaInformada = false;
                }
            } else {
                faltaPreenchimento = true;
            }
        });

        this.alocacaoSaldoDiasIncompleta = !faltaPreenchimento && quantidadeMinimaInformada && this.somaquantidadeDiasParcelas !== this.exercicioSelecionado.saldoDias;
    }

    validarPeriodos() {
        //Sempre coloca como false o periodoSobreposto da primeira parcela
        this.parcelasFerias.at(0).validacao.sobreposto = false;

        this.parcelasFerias.forEach(parcela => {
            if (parcela.dataFim) {
                //periodoSobreposto
                const parcelaSeguinte = this.parcelasFerias.at(parcela.ordem);
                if (parcelaSeguinte) {
                    parcelaSeguinte.validacao.sobreposto = false;
                    parcelaSeguinte.validacao.foraOrdem = false;
                    if (parcelaSeguinte.dataInicio && parcelaSeguinte.dataInicio.getTime() <= parcela.dataFim.getTime()) {
                        if (parcelaSeguinte.dataFim && parcelaSeguinte.dataFim.getTime() > parcela.dataFim.getTime()) {
                            parcelaSeguinte.validacao.sobreposto = true;
                        } else {
                            parcelaSeguinte.validacao.foraOrdem = true;
                        }
                    }
                }
            }

            //periodoForaJanelaProgramacao
            parcela.validacao.foraJanelaProgramacao = false;
            parcela.validacao.dataInvalida = false;
            if (!parcela.validacao.sobreposto) {
                if (parcela.dataInicio && parcela.dataInicio < this.dataMinimaParcela) {
                    parcela.validacao.foraJanelaProgramacao = true;
                    //Apaga a data de término
                    parcela.dataFim = undefined;
                } else if (this.regimeCeletistaOuAnistiado && parcela.dataFim && parcela.dataFim > this.dataMaximaParcela) {
                    //Só verifica a data de termino fora da janela de programação se for CLT/ANS
                    parcela.validacao.foraJanelaProgramacao = true;
                    //Apaga a data de término
                    parcela.dataFim = undefined;
                }
            }
        });
    }
}
